import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import ReactGA from 'react-ga4';
import ReactMarkdown from 'react-markdown';
import '../../../assets/css/App.css';
import JobviousLogo from '../../../assets/Jobvious_logo@2x.svg';

class EstimateChatbot extends Component {
  constructor(props) {
    super(props);

    this.state = {
      messages: [],
      inputValue: '',
      loading: true,
      sending: false,
      installerConfig: null,
      error: null,
      sessionId: 'estimate-session-' + Date.now(),
      quote: null,
      estimateId: null,
      campaignMetadata: null,
      campaignLoading: false
    };

    this.messagesEndRef = React.createRef();
  }

  async componentDidMount() {
    ReactGA.initialize(process.env.REACT_APP_GA);
    ReactGA.send({ hitType: "pageview", page: "/estimate" });

    // Extract installer ID from URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const installerId = urlParams.get('installer') || urlParams.get('id');

    // Parse URL parameters for attribution tracking
    this.initData = {
      referral_id: urlParams.get('rid') || urlParams.get('referral_id') || null,
      campaign_id: urlParams.get('cid') || urlParams.get('campaign_id') || null,
      customer_id: urlParams.get('customer_id') || null,
      utm_source: urlParams.get('utm_source') || null,
      utm_medium: urlParams.get('utm_medium') || null,
      utm_campaign: urlParams.get('utm_campaign') || null,
      gclid: urlParams.get('gclid') || null,
      fbclid: urlParams.get('fbclid') || null,
      gad_source: urlParams.get('gad_source') || null,
      gad_campaignid: urlParams.get('gad_campaignid') || null,
      installer_id: installerId ? parseInt(installerId) : null
    };

    if (!installerId) {
      this.setState({
        loading: false,
        error: 'Invalid URL: No installer ID specified. Please use: /estimate?installer=123'
      });
      return;
    }

    this.installerId = parseInt(installerId);

    // Load installer configuration
    await this.loadInstallerConfig();

    // Load campaign metadata if campaign_id is present
    await this.loadCampaignMetadata();

    // Track campaign click if applicable
    await this.trackCampaignClick();

    // Start chatbot conversation
    await this.kickOff();
  }

  componentDidUpdate() {
    this.scrollToBottom();
  }

  scrollToBottom = () => {
    if (this.messagesEndRef.current) {
      this.messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  getApiEndpoint() {
    return `${process.env.REACT_APP_API_URL}/chatbot/mcp`;
  }

  getConfigEndpoint() {
    return `${process.env.REACT_APP_API_URL}/api/installer`;
  }

  async loadInstallerConfig() {
    try {
      const response = await fetch(`${this.getConfigEndpoint()}/${this.installerId}/config`);

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to load installer configuration');
      }

      const config = await response.json();

      // Debug: Log the installer config to see what data we're getting
      console.log('=== INSTALLER CONFIG DEBUG ===');
      console.log('Full config:', config);
      console.log('Has google_rating:', !!config.google_rating, '→', config.google_rating);
      console.log('Has google_review_count:', !!config.google_review_count, '→', config.google_review_count);
      console.log('Has latitude:', !!config.latitude, '→', config.latitude);
      console.log('Has longitude:', !!config.longitude, '→', config.longitude);
      console.log('Has google_place_id:', !!config.google_place_id, '→', config.google_place_id);
      console.log('Has installer_phone_number:', !!config.installer_phone_number);
      console.log('Has installer_email:', !!config.installer_email);
      console.log('==============================');

      this.setState({ installerConfig: config });
    } catch (error) {
      console.error('Error loading installer config:', error);
      this.setState({
        error: 'Unable to load installer information: ' + error.message,
        loading: false
      });
    }
  }

  async loadCampaignMetadata() {
    // Only load if we have a campaign_id
    if (!this.initData.campaign_id) {
      return;
    }

    this.setState({ campaignLoading: true });

    try {
      const endpoint = `${process.env.REACT_APP_API_URL}/api/campaign/${this.initData.campaign_id}/metadata`;

      const response = await fetch(endpoint);

      if (response.ok) {
        const metadata = await response.json();
        console.log('Campaign metadata loaded:', metadata);
        this.setState({
          campaignMetadata: metadata,
          campaignLoading: false
        });
      } else {
        console.warn('Campaign metadata not found or inactive');
        this.setState({ campaignLoading: false });
      }
    } catch (error) {
      console.error('Error loading campaign metadata:', error);
      this.setState({ campaignLoading: false });
    }
  }

  async trackCampaignClick() {
    // Only track if we have campaign_id and customer_id, but NOT referral_id
    if (!this.initData.campaign_id || !this.initData.customer_id || this.initData.referral_id) {
      return;
    }

    try {
      const endpoint = `${process.env.REACT_APP_API_URL}/api/referral/campaign-click`;

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          campaign_id: this.initData.campaign_id,
          customer_id: this.initData.customer_id
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to track campaign click');
      }

      const data = await response.json();

      // Store the returned referral_id for use in chatbot session
      if (data.referral_id) {
        this.initData.referral_id = data.referral_id;
        console.log('Campaign click tracked. Referral ID:', data.referral_id);
      }
    } catch (error) {
      console.error('Error tracking campaign click:', error);
      // Don't block user experience if tracking fails
    }
  }

  async kickOff() {
    this.setState({ loading: true });

    try {
      const response = await fetch(this.getApiEndpoint(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: '',
          sessionId: this.state.sessionId,
          channel: 'chat',
          installer_id: this.installerId,
          initData: {
            ...this.initData,
            campaign_metadata: this.state.campaignMetadata
          }
        })
      });

      const data = await response.json();

      if (response.ok) {
        this.addBotMessage(data.message || 'Hello! How can I help you today?');

        if (data.quote) {
          this.setState({ quote: data.quote });
        }

        this.setState({ loading: false });
      } else {
        this.addBotMessage('Sorry, I encountered an error starting the session.');
        this.setState({
          loading: false,
          error: data.error || 'Unknown error'
        });
      }
    } catch (error) {
      console.error('Kickoff error:', error);
      this.addBotMessage('Sorry, cannot reach the server right now.');
      this.setState({
        loading: false,
        error: 'Connection error: ' + error.message
      });
    }
  }

  addBotMessage(text) {
    this.setState(prevState => ({
      messages: [...prevState.messages, { text, type: 'bot', timestamp: new Date() }]
    }));
  }

  addUserMessage(text) {
    this.setState(prevState => ({
      messages: [...prevState.messages, { text, type: 'user', timestamp: new Date() }]
    }));
  }

  handleInputChange = (e) => {
    this.setState({ inputValue: e.target.value });
  };

  handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      this.sendMessage();
    }
  };

  sendMessage = async () => {
    const message = this.state.inputValue.trim();
    if (!message || this.state.sending) return;

    this.addUserMessage(message);
    this.setState({ inputValue: '', sending: true });

    try {
      const response = await fetch(this.getApiEndpoint(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message,
          sessionId: this.state.sessionId,
          channel: 'chat',
          installer_id: this.installerId
        })
      });

      const data = await response.json();

      if (response.ok) {
        this.addBotMessage(data.message || '(no message)');

        if (data.quote) {
          this.setState({ quote: data.quote });
        }

        if (data.estimateId) {
          this.setState({ estimateId: data.estimateId });
        }
      } else {
        this.addBotMessage('Sorry, I encountered an error. Please try again.');
      }
    } catch (error) {
      console.error('Send error:', error);
      this.addBotMessage('Sorry, I cannot connect to the server right now. Please try again later.');
    } finally {
      this.setState({ sending: false });
    }
  };

  renderQuote() {
    const { quote } = this.state;
    if (!quote) return null;

    return (
      <div className="alert alert-warning my-3">
        <h6>Quote Context:</h6>
        {quote.vehicle && (
          <div>
            <strong>Vehicle:</strong> {[quote.vehicle.year, quote.vehicle.make, quote.vehicle.model].filter(Boolean).join(' ')}
            {quote.vehicle.sizeClass && <div><strong>Size Class:</strong> {quote.vehicle.sizeClass}</div>}
          </div>
        )}
        {quote.options && quote.options.length > 0 && (
          <div className="mt-2">
            <strong>Options:</strong>
            <ul className="mb-0 mt-1">
              {quote.options.map((option, idx) => (
                <li key={idx}>
                  {option.name || option.text || 'Option'}
                  {option.price != null && ` - $${Number(option.price).toLocaleString()}`}
                </li>
              ))}
            </ul>
            <em className="small">Say: first, second, or third.</em>
          </div>
        )}
      </div>
    );
  }

  render() {
    const { messages, inputValue, loading, sending, installerConfig, error, estimateId } = this.state;

    if (error && !installerConfig) {
      return (
        <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-md-8">
                <div className="card shadow">
                  <div className="card-body text-center p-5">
                    <h3 className="text-danger mb-3">Error</h3>
                    <p>{error}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="min-vh-100 bg-light">
        <div className="container py-4">
          <div className="row justify-content-center">
            <div className="col-lg-10">
              {/* Header */}
              <div className="text-center mb-5">
                <p className="text-muted mb-1" style={{ fontSize: '1.4rem', fontWeight: '400' }}>
                  Schedule An Appointment With
                </p>
                <h1 className="display-4 mb-4" style={{ fontWeight: 'bold' }}>
                  {installerConfig?.name || 'Get Your Estimate'}
                </h1>

                {installerConfig && (
                  <>
                    {/* Contact Information Bar */}
                    <div className="d-flex flex-wrap justify-content-center mb-4">
                      <span className="text-muted mx-3">
                        <i className="fa fa-map-marker mr-2"></i>
                        {[installerConfig.city, installerConfig.state].filter(Boolean).join(', ')}
                      </span>
                      {installerConfig.phone && (
                        <span className="mx-3">
                          <i className="fa fa-phone mr-2"></i>
                          <a href={`tel:${installerConfig.phone}`} className="text-decoration-none">
                            {installerConfig.phone}
                          </a>
                        </span>
                      )}
                      {installerConfig.email && (
                        <span className="mx-3">
                          <i className="fa fa-envelope mr-2"></i>
                          <a href={`mailto:${installerConfig.email}`} className="text-decoration-none">
                            {installerConfig.email}
                          </a>
                        </span>
                      )}
                    </div>

                    <p className="lead text-muted mb-0">Get Your Personalized Quote in Minutes</p>
                  </>
                )}
              </div>

              {/* Campaign Offer Banner */}
              {this.state.campaignMetadata && this.state.campaignMetadata.campaign_description && (
                <div className="alert mb-4" style={{
                  backgroundColor: '#d4edda',
                  borderColor: '#c3e6cb',
                  borderRadius: '8px',
                  border: '2px solid #28a745'
                }}>
                  <div className="d-flex align-items-start">
                    <div className="mr-3">
                      <i className="fa fa-gift fa-2x text-success"></i>
                    </div>
                    <div className="flex-grow-1">
                      <h5 className="mb-1" style={{ color: '#155724' }}>
                        <strong>Special Offer</strong>
                        {this.state.campaignMetadata.promo_code && (
                          <span className="badge badge-success ml-2" style={{ fontSize: '12px' }}>
                            {this.state.campaignMetadata.promo_code}
                          </span>
                        )}
                      </h5>
                      <p className="mb-1" style={{ color: '#155724' }}>
                        {this.state.campaignMetadata.campaign_description}
                      </p>
                      {this.state.campaignMetadata.discount_description && (
                        <p className="mb-0">
                          <strong style={{ color: '#155724' }}>
                            <i className="fa fa-tag mr-1"></i>
                            {this.state.campaignMetadata.discount_description}
                          </strong>
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Chat Container */}
              <div className="card shadow">
                <div className="card-body">
                  {/* Messages Area */}
                  <div
                    className="border rounded p-3 mb-3 bg-light"
                    style={{ height: '420px', overflowY: 'auto' }}
                  >
                    {loading && messages.length === 0 ? (
                      <div className="text-center text-muted py-5">
                        <div className="spinner-border mb-3" role="status">
                          <span className="sr-only">Loading...</span>
                        </div>
                        <div>Initializing...</div>
                      </div>
                    ) : (
                      <>
                        {messages.map((msg, idx) => (
                          <div
                            key={idx}
                            className={`mb-3 ${msg.type === 'user' ? 'text-right' : ''}`}
                          >
                            <div
                              className={`d-inline-block p-2 rounded ${
                                msg.type === 'user'
                                  ? 'bg-primary text-white'
                                  : 'bg-white border'
                              }`}
                              style={{ maxWidth: '80%', textAlign: 'left' }}
                            >
                              <strong>
                                {msg.type === 'user' ? 'You' : (installerConfig?.name || 'Assistant')}:
                              </strong>
                              <div className={`mt-1 ${msg.type === 'bot' ? 'chatbot-message' : ''}`}>
                                {msg.type === 'bot' ? (
                                  <ReactMarkdown>{msg.text}</ReactMarkdown>
                                ) : (
                                  msg.text
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                        {sending && (
                          <div className="mb-3">
                            <div
                              className="d-inline-block p-2 rounded bg-white border"
                              style={{ maxWidth: '80%', textAlign: 'left' }}
                            >
                              <strong>{installerConfig?.name || 'Assistant'}:</strong>
                              <div className="typing-indicator">
                                <span></span>
                                <span></span>
                                <span></span>
                              </div>
                            </div>
                          </div>
                        )}
                        <div ref={this.messagesEndRef} />
                      </>
                    )}
                  </div>

                  {/* Quote Display */}
                  {this.renderQuote()}

                  {/* Estimate ID Display */}
                  {estimateId && (
                    <div className="alert alert-success">
                      <strong>Estimate Created:</strong> ID {estimateId}
                    </div>
                  )}

                  {/* Input Area */}
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Type your message here..."
                      value={inputValue}
                      onChange={this.handleInputChange}
                      onKeyPress={this.handleKeyPress}
                      disabled={loading || sending}
                    />
                    <div className="input-group-append">
                      <button
                        className="btn btn-primary"
                        onClick={this.sendMessage}
                        disabled={loading || sending || !inputValue.trim()}
                      >
                        {sending ? 'Sending...' : 'Send'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Location Section */}
              {installerConfig && installerConfig.name && (
                <div className="card shadow mt-4">
                  <div className="card-header bg-white">
                    <h5 className="mb-0">
                      <i className="fa fa-map-marker text-primary mr-2"></i>
                      Our Location
                    </h5>
                  </div>
                  <div className="card-body p-0">
                    <iframe
                      width="100%"
                      height="450"
                      frameBorder="0"
                      style={{ border: 0 }}
                      src={installerConfig.google_place_id
                        ? `https://www.google.com/maps/embed/v1/place?key=${process.env.REACT_APP_GOOGLE_API_KEY}&q=place_id:${installerConfig.google_place_id}`
                        : `https://maps.google.com/maps?q=${installerConfig.latitude},${installerConfig.longitude}&z=15&output=embed`
                      }
                      allowFullScreen
                      title={`${installerConfig.name} Location`}
                      loading="lazy"
                    />
                  </div>
                  <div className="card-footer bg-white text-center">
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=Google&query_place_id=${installerConfig.google_place_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-primary"
                    >
                      <i className="fa fa-external-link mr-1"></i>
                      View on Google Maps
                    </a>
                  </div>
                </div>
              )}

              {/* Powered by Jobvious Footer */}
              <div className="text-center mt-4 py-3">
                <div className="d-flex align-items-center justify-content-center gap-2">
                  <span className="text-muted">Powered by</span>
                  <img
                    src={JobviousLogo}
                    alt="Jobvious"
                    style={{ height: '30px' }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default withRouter(EstimateChatbot);
